import { defineStore } from 'pinia'
import {
  isApiNotFoundError,
  isApiUnauthorizedError,
  isAuthTokenUnavailableError,
  type ApiSchema,
} from '~/api/client'
import { listAgents } from '~/api/agents'
import {
  getCustomerPlan,
  getCustomerTeamWorkflowSettings,
} from '~/api/customers'
import {
  getCurrentUserId,
  getUser,
  listRoles,
  updateUser,
} from '~/api/users'
import {
  mapAgent,
  mapCustomerPlan,
  mapCustomerTeamWorkflowSettings,
  type Agent,
  type CustomerPlan,
  type CustomerTeamWorkflowSettings,
} from '~/api/mappers'
import { useImpersonateStore } from '~/stores/impersonate'
import { analytics } from '~/services/analytics'
import { resetHelpCenterIdentity } from '~/services/helpCenterIdentity'
import { getAuth } from 'firebase/auth'

const userDataFetches = new WeakMap<object, Promise<void>>()

export const useUserStore = defineStore('user', {
  state: () => ({
    userFirstName: '',
    userLastName: '',
    userName: '',
    userEmail: '',
    userId: '',
    customerId: '',
    customerName: '', // Kundennamen speichern
    roleId: '', // Role ID speichern
    roleName: '', // Rollennamen speichern
    visibleExternalCustomers: [] as string[],
    billingPageVisible: true,
    phoneNumber: '', // Telefonnummer speichern
    /** ISO 639-1 (e.g. de, en). */
    language: '',
    /** BCP 47 (e.g. de-DE, en-US). */
    locale: '',
    /** IANA timezone (server may default). */
    timezone: '',
    hideCallsWithoutCallerSpeech: false,
    /**
     * Server-managed last activity (UTC); updated on authenticated requests, not editable by clients.
     * ISO string when set; for display use useUserFormatting().formatDateTime.
     */
    lastActivityAt: null as string | null,
    customerPlan: null as CustomerPlan | null, // Plan-Daten als ein einziges Objekt
    customerTeamWorkflowSettings: null as CustomerTeamWorkflowSettings | null,
    restSemaphore: -1,
    roles: [] as Array<{ id: string, name: string }>, // Liste der Rollen
    agents: [] as Agent[], // Neues Array für Agenten
  }),
  getters: {
    /** Plan of the currently watched customer (own plan or impersonated customer's plan).
     * Use this getter to get the plan of the currently watched customer.
     */
    effectiveCustomerPlan(state): CustomerPlan | null {
      const impersonateStore = useImpersonateStore()
      if (impersonateStore.isImpersonating && impersonateStore.impersonatedCustomerPlan) {
        return impersonateStore.impersonatedCustomerPlan
      }
      return state.customerPlan
    },
    effectiveCallCommentsEditable(state): boolean {
      const impersonateStore = useImpersonateStore()
      if (
        impersonateStore.isImpersonating
        && impersonateStore.impersonatedTeamWorkflowSettings
      ) {
        return impersonateStore.impersonatedTeamWorkflowSettings.callCommentsEditable !== false
      }
      return state.customerTeamWorkflowSettings?.callCommentsEditable !== false
    },
    effectiveCallerNumberDialFormat(state): ApiSchema<'CallerNumberDialFormat'> {
      const impersonateStore = useImpersonateStore()
      if (
        impersonateStore.isImpersonating
        && impersonateStore.impersonatedTeamWorkflowSettings
      ) {
        return impersonateStore.impersonatedTeamWorkflowSettings.callerNumberDialFormat
          ?? 'e164_plus'
      }
      return state.customerTeamWorkflowSettings?.callerNumberDialFormat ?? 'e164_plus'
    },
  },
  actions: {
    async fetchAgents(customerId: string | null = null) {
      $log('Fetching agents...')

      // Importiere den Impersonate Store
      const impersonationStore = useImpersonateStore()

      // Dynamisch effektive Customer-ID auswählen: Falls Impersonation aktiv ist, wird die impersonatedCustomerId verwendet, ansonsten der übergebene Parameter
      const effectiveCustomerId
        = impersonationStore.isImpersonating
          && impersonationStore.impersonatedCustomerId
          ? impersonationStore.impersonatedCustomerId
          : customerId

      // Build the query parameters
      const requestParams: { customer_id?: string } = {}
      if (effectiveCustomerId) {
        requestParams.customer_id = effectiveCustomerId
      }

      $log('Request Params:', requestParams)

      try {
        const agentsData = await listAgents(requestParams)

        if (agentsData) {
          this.agents = agentsData.map(mapAgent) // Store agents in state
          $log('Agents stored in state:', this.agents)
          return this.agents // Return the fetched data
        }
        else {
          $logError('No agents data found in the API response.')
          return [] // Return an empty array if no data is found
        }
      }
      catch (error) {
        $logError('Error fetching agents:', error)
        return [] // Return an empty array in case of errors
      }
    },

    syncAnalyticsProfile() {
      analytics.capture(
        '$set',
        {
          $set: {
            userFirstName: this.userFirstName,
            userLastName: this.userLastName,
            customerId: this.customerId,
            customerName: this.customerName,
            roleName: this.roleName,
            roleId: this.roleId,
            language: this.language,
            locale: this.locale,
            timezone: this.timezone,
          },
        },
      )
    },

    async fetchUserData(): Promise<void> {
      const existingFetch = userDataFetches.get(this)
      if (existingFetch) {
        return existingFetch
      }

      const fetch = this.fetchUserDataUncached()
      userDataFetches.set(this, fetch)
      try {
        await fetch
      }
      finally {
        if (userDataFetches.get(this) === fetch) {
          userDataFetches.delete(this)
        }
      }
    },

    async fetchUserDataUncached(): Promise<void> {
      $log('Fetching user data...')

      try {
        $log('Fetching user ID...')

        // Own profile must not inherit an active impersonation session.
        const userIdData = await getCurrentUserId({ impersonation: false })

        $log('User ID response:', userIdData)

        if (userIdData && userIdData.id) {
          this.userId = userIdData.id
          $log('User ID:', this.userId)

          const auth = getAuth()
          const user = auth.currentUser

          if (!user) {
            $logError('No authenticated user found.')
            return
          }

          // PostHog identify
          analytics.identify(
            user.uid, // unique identifier for the user
            {
              email: auth.currentUser.email,
              language: this.language,
              projectId: import.meta.env.MODE,
              firebaseDisplayName: auth.currentUser.displayName,
              firebasePhoneNumber: auth.currentUser.phoneNumber,
              firebaseCreationTime: auth.currentUser.metadata.creationTime,
              firebaseLastLoginTime: auth.currentUser.metadata.lastSignInTime }, // optional: set additional person properties
          )

          // Benutzerinformationen über den /users/{user_id} Endpunkt abrufen
          $log(`Fetching user data for user ID: ${this.userId}...`)
          const userData = await getUser(this.userId, { impersonation: false })

          $log('User data response:', userData)

          if (userData) {
            const impersonateStore = useImpersonateStore()
            this.userFirstName = userData.first_name ?? ''
            this.userLastName = userData.last_name ?? ''
            this.userName
              = `${userData.first_name ?? ''} ${userData.last_name ?? ''}`.trim()
            this.userEmail = userData.email ?? ''
            this.customerId = userData.customer_id ?? ''
            this.customerName = userData.customer_name ?? '' // Kundennamen speichern
            this.roleName = userData.role_name ?? '' // Rollennamen speichern
            this.roleId = userData.role_id ?? '' // Role ID speichern
            this.visibleExternalCustomers = userData.visible_external_customers ?? []
            this.billingPageVisible = userData.billing_page_visible ?? true
            this.phoneNumber = userData.phone_number ?? '' // Telefonnummer speichern

            this.language = userData.language ?? ''
            this.locale = userData.locale ?? ''
            this.timezone = userData.timezone ?? ''
            this.hideCallsWithoutCallerSpeech
              = userData.hide_calls_without_caller_speech ?? false
            this.lastActivityAt = userData.last_activity_at
              ? new Date(userData.last_activity_at).toISOString()
              : null
            this.syncAnalyticsProfile()

            await impersonateStore.fetchContext()

            // Fetch additional data in parallel
            await Promise.all([
              this.fetchPlanDetails(),
              this.fetchTeamWorkflowSettings(),
              this.fetchRoles(),
              this.fetchAgents(this.customerId),
            ])

            analytics.group('customer', this.customerId, {
              customer_name: this.customerName,
              role_name: this.roleName,
              free_mins_per_month: this.customerPlan?.freeMinsPerMonth,
              number_of_agents: this.customerPlan?.numberOfAgents,
              team_call_management_available:
                this.customerPlan?.teamCallManagementAvailable,
              integrations_available: this.customerPlan?.integrationsAvailable,
              crm_csv_data_import: this.customerPlan?.crmCsvDataImport,
            })
          }
          else {
            $logError('No user data found in the API response.')
          }
        }
        else {
          $logError('No user ID found in the API response.')
        }
      }
      catch (error) {
        if (isAuthTokenUnavailableError(error) || isApiUnauthorizedError(error)) {
          $log('Skipping user data fetch because the auth session is unavailable.')
          return
        }
        if (isApiNotFoundError(error)) {
          $log('Signing out because the authenticated Firebase user has no backend profile.')
          await getAuth().signOut()
          analytics.reset()
          resetHelpCenterIdentity()
          return
        }
        $reportError('Error fetching user data:', error)
      }
    },

    /** Fetches plan for a customer from the backend. Used for own plan (fetchPlanDetails) and for impersonation (caller sets result on impersonate store).
     * Only use this to get the current customer's plan form the backend. Do not use it to just check current customer's plan.
     * Use the getter effectiveCustomerPlan instead.
     * @param customerId - The ID of the customer to fetch the plan for.
     * @returns The plan for the customer or null if the plan could not be fetched.
     * @throws An error if the plan could not be fetched.
     */
    async getPlanForCustomer(customerId: string): Promise<CustomerPlan | null> {
      try {
        const planData = await getCustomerPlan(customerId)
        return mapCustomerPlan(planData)
      }
      catch (error) {
        $logError('Error fetching plan details for customer:', error)
        return null
      }
    },

    async getTeamWorkflowSettingsForCustomer(
      customerId: string,
    ): Promise<CustomerTeamWorkflowSettings | null> {
      try {
        const settingsData = await getCustomerTeamWorkflowSettings(customerId)
        return mapCustomerTeamWorkflowSettings(settingsData)
      }
      catch (error) {
        $logError('Error fetching team workflow settings for customer:', error)
        return null
      }
    },

    async fetchTeamWorkflowSettings(): Promise<void> {
      $log('Fetching team workflow settings...')
      const settingsData = await this.getTeamWorkflowSettingsForCustomer(this.customerId)
      if (settingsData) {
        this.customerTeamWorkflowSettings = settingsData
        $log('Team workflow settings stored in state:', this.customerTeamWorkflowSettings)
      }
      else {
        $logError('No team workflow settings found in the API response.')
      }
    },

    async fetchPlanDetails(): Promise<void> {
      $log('Fetching plan details...')
      const planData = await this.getPlanForCustomer(this.customerId)
      if (planData) {
        this.customerPlan = planData
        $log('Plan details stored in state:', this.customerPlan)
      }
      else {
        $logError('No plan data found in the API response.')
      }
    },

    async updateHideCallsWithoutCallerSpeech(value: boolean): Promise<void> {
      if (!this.userId) {
        throw new Error('User ID is missing.')
      }

      const updated = await updateUser(this.userId, {
        hide_calls_without_caller_speech: value,
      })
      this.hideCallsWithoutCallerSpeech
        = updated.hide_calls_without_caller_speech ?? value
    },

    // Neue Methode zum Abrufen der Rollen
    async fetchRoles(): Promise<void> {
      $log('Fetching roles...')

      try {
        const rolesData = await listRoles()

        this.roles = rolesData.map(role => ({
          id: role.id,
          name: role.name,
        }))

        $log('Roles stored in state:', this.roles)
      }
      catch (error) {
        $logError('Error fetching roles:', error)
      }
    },
  },
  persist: true,
})
